if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.sectionId = this.dataset.sectionId;

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const createConfig = (body) => {
          const cfg = fetchConfig('javascript');
          cfg.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete cfg.headers['Content-Type'];
          cfg.body = body;
          return cfg;
        };

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        const addExtraVariant = (variantId, isWarranty = false, warrantyData = null) => {
          const fd = new FormData();
          fd.append('id', variantId);
          fd.append('quantity', 1);

          // Add warranty properties if this is a warranty item
          if (isWarranty && warrantyData) {
            fd.append('properties[_warranty_for_product_id]', warrantyData.productId);
            fd.append('properties[_warranty_for_variant_id]', warrantyData.variantId);

            // Build detailed warranty description with variant info
            let warrantyDescription = warrantyData.productTitle;

            // Add model code if available
            if (warrantyData.modelCode) {
              warrantyDescription += ` ${warrantyData.modelCode}`;
            }

            // Add variant options if not default
            const variantOptions = [];
            if (warrantyData.option1 && warrantyData.option1 !== 'Default Title') {
              variantOptions.push(warrantyData.option1);
            }
            if (warrantyData.option2 && warrantyData.option2 !== 'Default Title') {
              variantOptions.push(warrantyData.option2);
            }
            if (warrantyData.option3 && warrantyData.option3 !== 'Default Title') {
              variantOptions.push(warrantyData.option3);
            }

            if (variantOptions.length > 0) {
              warrantyDescription += ` (${variantOptions.join(', ')})`;
            }

            fd.append('properties[Garantia para]', warrantyDescription);
          }

          // Do not request sections for extras; the main add will re-render sections
          return fetch(`${routes.cart_add_url}`, createConfig(fd)).then((r) => r.json());
        };

        const selectedExtras = this.getSelectedExtraItems();
        const addExtrasSequentially = selectedExtras.reduce((promise, extraItem) => {
          return promise.then(() => addExtraVariant(extraItem.variantId, extraItem.isWarranty, extraItem.warrantyData));
        }, Promise.resolve());

        addExtrasSequentially
          .then(() => fetch(`${routes.cart_add_url}`, createConfig(formData)))
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      getSelectedExtraItems() {
        try {
          if (!this.sectionId) return [];
          const container = document.getElementById(`ProductInfo-${this.sectionId}`) || this.closest(`#ProductInfo-${this.sectionId}`);
          if (!container) return [];
          const inputs = container.querySelectorAll('.extra-item-checkbox:checked');

          return Array.from(inputs).map((input) => {
            const isWarranty = input.classList.contains('warranty-item');
            const extraItem = {
              variantId: input.value,
              isWarranty: isWarranty,
              warrantyData: null
            };

            if (isWarranty) {
              extraItem.warrantyData = {
                productId: input.dataset.warrantyProductId,
                variantId: input.dataset.warrantyVariantId,
                productTitle: input.dataset.warrantyProductTitle,
                modelCode: input.dataset.warrantyProductModelCode,
                option1: input.dataset.warrantyVariantOption1,
                option2: input.dataset.warrantyVariantOption2,
                option3: input.dataset.warrantyVariantOption3
              };
            }

            return extraItem;
          }).filter(item => item.variantId);
        } catch (e) {
          return [];
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
